import React from "react";
import { graphql, QueryRenderer } from "react-relay";
import { Link, type LinkProps } from "react-router-dom";

import { faUserFriends } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Dimmer, Loader, Popup } from "semantic-ui-react";

import ViewportContext from "~/common/ViewportContext";
import { hasOwnProperty } from "~/lib/js";
import { smart } from "~/lib/relayUtil";
import { environment } from "~/models";
import Avatar from "~/ui/legacy/Avatar";
import type { UserAttrs } from "~/ui/legacy/Avatar";

import type { UserPopup_user$data as UserPopupUser } from "~/__generated__/UserPopup_user.graphql";
import type { UserPopupQuery } from "~/__generated__/UserPopupQuery.graphql";

import "./css/UserPopup.css";

interface UserPopupContentsProps {
	user: UserPopupUser;
	viewer: UserPopupQuery["response"]["viewer"];
	onClick: () => void;
}

const UserPopupContents = smart<UserPopupContentsProps>(
	class UserPopupContents extends React.Component<UserPopupContentsProps> {
		static Fragments = {
			user: graphql`
				fragment UserPopup_user on User {
					id
					firstName
					fullName
					lastName
					email
					image
					title
					department
					isActive
					isGuest
					relationshipToViewer
					isTopUser
					internalAttributes {
						oneOnOneWithViewer {
							id
						}
						manager {
							id
							fullName
							firstName
							lastName
							email
							image
							isTopUser
						}
					}
				}
			`,
		};

		render(): React.ReactNode {
			const { viewer, user } = this.props;

			return (
				<Card className="user-card user-card--link" fluid>
					<Card.Content>
						<Avatar user={user} link={false} />
						<div className="details">
							<Card.Header>
								<Link to={`/profile/${user.id}/`} className="user-name">
									{user.fullName}
								</Link>
							</Card.Header>
							{!user.isActive && (
								<div>
									<em>Inactive</em>
								</div>
							)}
							{user.isActive && user.title && <div>{user.title}</div>}
							{user.isActive && user.department && <div>{user.department}</div>}
							{user.isActive && user.internalAttributes?.manager && (
								<div>
									{"Reports to "}
									<Link to={`/profile/${user.internalAttributes.manager.id}/`} className="user-name">
										{user.internalAttributes.manager.fullName}
									</Link>
								</div>
							)}
						</div>
					</Card.Content>
					{user.isActive && viewer.user && viewer.user.id != user.id && user.internalAttributes?.oneOnOneWithViewer && (
						<Card.Content extra className="with-actions">
							<div className="right floated actions">
								<Link className="ui action-item" to={`/1on1s/${viewer.user.id}/${user.id}/`} title="Go to 1-on-1s">
									<FontAwesomeIcon icon={faUserFriends} />
								</Link>
							</div>
						</Card.Content>
					)}
				</Card>
			);
		}
	},
);

interface UserPopupProps {
	user: UserAttrs;
	openDelay: number;
	closeDelay: number;
	as: string | React.ComponentType<LinkProps | React.HTMLAttributes<HTMLElement>>;
	on?: "click" | "hover" | "focus" | ("click" | "hover" | "focus")[];
	position?:
		| "bottom center"
		| "top left"
		| "top right"
		| "bottom right"
		| "bottom left"
		| "right center"
		| "left center"
		| "top center";
	size?: "small" | "mini" | "tiny" | "large" | "huge";
	useWrapper?: boolean;
}

interface UserPopupState {
	isPopupOpen: boolean;
}

export default class UserPopup extends React.Component<UserPopupProps, UserPopupState> {
	static contextType = ViewportContext;
	declare context: React.ContextType<typeof ViewportContext>;

	static Query = graphql`
		query UserPopupQuery($userId: ID!) {
			viewer {
				user {
					id
				}
			}
			user: node(id: $userId) {
				...UserPopup_user
			}
		}
	`;

	closeTimer = 0;
	openTimer = 0;

	state: UserPopupState = {
		isPopupOpen: false,
	};

	static defaultProps = {
		openDelay: 200,
		closeDelay: 0,
		on: ["click"],
		position: "bottom center",
		as: "span",
	};

	openPopup = (): void => {
		window.clearTimeout(this.closeTimer);
		this.openTimer = window.setTimeout(this.onPopupOpen, this.props.openDelay);
	};

	onPopupOpen = (): void => this.setState({ isPopupOpen: true });

	closePopup = (): void => {
		window.clearTimeout(this.openTimer);
		this.closeTimer = window.setTimeout(this.onPopupClose, this.props.closeDelay);
	};

	onPopupClose = (): void => this.setState({ isPopupOpen: false });

	closeCreateNoteDialog = (): void => {
		this.closePopup();
	};

	renderUserPopupContents = ({ props }: { props: UserPopupQuery["response"] | null }) => {
		if (!props) return null;

		const { viewer, user } = props;
		if (!viewer || !user) return null;

		return (
			<React.Fragment>
				<Dimmer inverted active={!this.state.isPopupOpen || !props}>
					<Loader inverted />
				</Dimmer>
				{props && <UserPopupContents onClick={this.closePopup} viewer={viewer} user={user} />}
			</React.Fragment>
		);
	};

	render(): React.ReactNode {
		const { user, size, position, useWrapper } = this.props;

		let children = this.props.children as React.ReactElement | React.ReactElement[];
		let WrapperType = this.props.as;
		let onEvent = this.props.on;

		if (hasOwnProperty(children, "length") || useWrapper) {
			if (this.props.as == Link) WrapperType = "span"; // Disable all links
			const ComponentWrapper: string | React.ComponentType<React.HTMLAttributes<HTMLElement>> = WrapperType;
			children = <ComponentWrapper className="user-popup-trigger" children={this.props.children} />;
		}

		let newChildren = children as React.ReactElement;
		if (WrapperType == Link || WrapperType == "a") onEvent = ["hover"];

		if (onEvent && onEvent.includes("click")) {
			const childrenStyle = newChildren.props.style || {};
			newChildren = React.cloneElement(newChildren, { style: { ...childrenStyle, cursor: "pointer" } });
		}

		if (this.context.isMobile) {
			return newChildren;
		}

		return (
			<Popup
				flowing
				className="user-popup"
				hoverable
				on={onEvent}
				onClose={this.closePopup}
				onOpen={this.openPopup}
				open={this.state.isPopupOpen}
				trigger={newChildren}
				closeOnTriggerBlur
				mouseEnterDelay={1000}
				mouseLeaveDelay={1000}
				position={position}
				size={size}
			>
				{user.id ? (
					<QueryRenderer<UserPopupQuery>
						query={UserPopup.Query}
						environment={environment}
						variables={{ userId: user.id }}
						render={this.renderUserPopupContents}
					/>
				) : (
					<Card className="user-card user-card--link" fluid>
						<Card.Content>
							<Avatar user={user} link={false} />
							<div className="details">
								<Card.Header>
									<span className="user-name">{user.fullName}</span>
								</Card.Header>
							</div>
						</Card.Content>
					</Card>
				)}
			</Popup>
		);
	}
}
